import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { FormattedMessage, useIntl } from "react-intl";
import IconButton from "@mui/material/IconButton";
import { Box, Divider, Tooltip } from "@mui/material";
import formatCurrency from "../utils/currencyFormat";
import { getFormattedDate } from "../utils/getFormattedDate";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartTwoTone";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { formattedTranslationValue } from "../utils/formatted-translation-value";
import { isHTMLEmpty } from "../utils/isHtml";
import { calculateIsDarkColor } from "../utils/color";
import TagManager from "react-gtm-module";
import PopularIcon from "./PopularIcon";

export const isOutOfStock = (pp, pkg, cart) => {
	if (!Boolean(pp) || !Boolean(pkg)) return true;
	const stock = pkg.stock;
	const prevOrdsAmount = pkg.prevOrdsAmount;
	const curOrdAmount =
		cart.find((item) => item.id === pp?.id)?.quantity ?? 0 + 1;
	const totalAmount = prevOrdsAmount + curOrdAmount;
	return (stock && stock <= totalAmount) || pkg?.noStock;
};

const Package = ({
	pkg,
	currency,
	form,
	language,
	color,
	onCartUpdate,
	cart,
	setCartOpen,
	free,
}) => {
	const intl = useIntl();
	const activePeriod = pkg?.activePrice;
	const defaultPeriod = pkg?.defaultPrice;
	const eventButtonColor = calculateIsDarkColor(color) ? "#fff" : "#000";

	const [open, setOpen] = useState(false);
	const [selectedPkg, setSelectedPkg] = useState();

	useEffect(() => {
		if (open && form?.event && selectedPkg) {
			TagManager.dataLayer({
				dataLayer: {
					event: "kapital_events_view_content_ticket",
					kapital_events_event_name: form?.event?.name,
					kapital_events_ticket_type: formattedTranslationValue(
						selectedPkg?.translations,
						"name",
						language
					),
				},
			});
		}
	}, [open, form, selectedPkg, language]);

	const handleOpen = () => setOpen(true);
	const handleClose = () => {
		setOpen(false);
		setSelectedPkg(null);
	};

	const hasDetails = (translations) =>
		!isHTMLEmpty(formattedTranslationValue(translations, "description", language));

	const discountPercent =
		activePeriod &&
		defaultPeriod &&
		!activePeriod.isDefault &&
		defaultPeriod.price > 0
			? Math.round((1 - activePeriod.price / defaultPeriod.price) * 100)
			: 0;

	const eyebrow = formattedTranslationValue(pkg.translations, "title", language);

	const addToCart = (per, pkg) => {
		onCartUpdate({
			id: per.id,
			description: `${pkg.description} (${per.description})`,
			price: free ? 0 : per.price,
			package: pkg,
			stock: pkg.stock,
			currencyCode: currency,
		});
	};

	return (
		<>
			{pkg.id && (
				<Box className="ticket-card" sx={{ position: "relative" }}>
					{pkg.isPopular && (
						<Box className="ticket-card__seal">
							<PopularIcon fill={color} width={64} height={64} />
							<Typography
								variant="span"
								className="ticket-card__seal-label"
								sx={{ color: eventButtonColor }}
							>
								<FormattedMessage id="populer" />
							</Typography>
						</Box>
					)}
					<Box className="ticket-card__frame">
						<Box className="ticket-card__spine" />
						{/*
							Name stays close to the stub divider; whatever extra room a
							row forces on this card stretches the price block instead
							(centered between the stub and the buy button) -- same
							composition as the main registration page's ticket cards.
						*/}
						<Box className="ticket-card__body" sx={{ textAlign: "center" }}>
							{eyebrow && (
								<Box
									className="ticket-card__eyebrow"
									sx={{ backgroundColor: color, color: eventButtonColor }}
								>
									<Typography variant="span" className="ticket-card__eyebrow-text">
										{eyebrow}
									</Typography>
								</Box>
							)}

							<Typography
								variant="span"
								className="ticket-card__name"
								sx={pkg.isPopular ? { pr: "52px" } : undefined}
							>
								{formattedTranslationValue(pkg.translations, "name", language)}
							</Typography>

							<Box className="ticket-card__stub" aria-hidden="true" />

							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
								}}
							>
								{activePeriod && defaultPeriod && (
									<Box className="ticket-card__price-block">
										{!activePeriod?.isDefault && (
											<Box className="ticket-card__price-row">
												<Typography variant="span" className="ticket-card__price-old">
													{formatCurrency(defaultPeriod?.price, currency, intl, free)}
												</Typography>
												{discountPercent > 0 && (
													<Box className="ticket-card__discount-chip">
														-%{discountPercent}
													</Box>
												)}
											</Box>
										)}
										<Typography variant="span" className="ticket-card__price">
											{formatCurrency(activePeriod?.price, currency, intl, free)}
										</Typography>
										{!activePeriod?.isDefault && (
											<Box className="ticket-card__period">
												<Box className="ticket-card__period-range">
													<Typography variant="span" className="ticket-card__period-date">
														{getFormattedDate(activePeriod?.startDate)}
													</Typography>
													<ChevronRightIcon sx={{ fontSize: 14 }} />
													<Typography variant="span" className="ticket-card__period-date">
														{getFormattedDate(activePeriod?.endDate)}
													</Typography>
												</Box>
												{activePeriod?.description && (
													<Typography variant="span" className="ticket-card__period-label">
														{activePeriod.description}
													</Typography>
												)}
											</Box>
										)}
									</Box>
								)}
							</Box>

							<Box className="ticket-card__actions">
								{hasDetails(pkg.translations) && (
									<Button
										startIcon={<InfoOutlinedIcon fontSize="small" />}
										onClick={() => {
											setSelectedPkg(pkg);
											handleOpen();
										}}
										className="ticket-card__details"
										sx={{ color }}
									>
										<FormattedMessage id="detail" />
									</Button>
								)}
								{isOutOfStock(activePeriod, pkg, cart) ? (
									<Button
										variant="contained"
										disabled
										startIcon={<DoNotDisturbIcon />}
										className="ticket-card__cta no-toggle"
									>
										<FormattedMessage id="no-stock" />
									</Button>
								) : (
									<Button
										variant="contained"
										startIcon={<ShoppingCartIcon />}
										className="ticket-card__cta no-toggle"
										sx={{
											backgroundColor: color,
											color: eventButtonColor,
											"&:hover": { opacity: 0.85, backgroundColor: color },
										}}
										onClick={() => {
											addToCart(activePeriod, pkg);
											setCartOpen(true);
										}}
									>
										<FormattedMessage id="buy" />
									</Button>
								)}
							</Box>
						</Box>
						<Box className="ticket-card__barcode">
							<Typography variant="span" className="ticket-card__barcode-code">
								{form?.event?.name}
							</Typography>
						</Box>
					</Box>
				</Box>
			)}
			{/* Children Packages */}
			{pkg.children.length > 0 && (
				<Box className="ticket-card ticket-card--group" sx={{ position: "relative" }}>
					<Box className="ticket-card__frame">
						<Box className="ticket-card__spine" />
						<Box className="ticket-card__body">
							<Typography
								variant="span"
								className="ticket-card__name"
								sx={{ textAlign: "center" }}
							>
								{formattedTranslationValue(pkg.translations, "childrenGroupTitle", language)}
							</Typography>

							<Box className="ticket-card__stub" aria-hidden="true" />

							<Box className="ticket-card__list">
								{pkg.children.map((p) => {
									const childActivePeriod = p.activePrice;
									return (
										<Box className="ticket-card__list-item" key={p.id}>
											<Box className="ticket-card__list-item-info">
												<Typography variant="span" className="ticket-card__list-item-name">
													{formattedTranslationValue(p.translations, "name", language)}
												</Typography>
												<Typography variant="span" className="ticket-card__list-item-price">
													{formatCurrency(childActivePeriod?.price, currency, intl, free)}
												</Typography>
											</Box>
											<Box display="flex" alignItems="center" gap={0.5}>
												{hasDetails(p.translations) && (
													<IconButton
														aria-label={intl.formatMessage({ id: "detail" })}
														size="small"
														className="ticket-card__list-item-icon"
														onClick={() => {
															setSelectedPkg(p);
															handleOpen();
														}}
													>
														<InfoOutlinedIcon fontSize="small" />
													</IconButton>
												)}
												{isOutOfStock(childActivePeriod, p, cart) ? (
													// Devre dışı buton fare olaylarını almadığı için ipucu
													// span üzerinde; mobilde dokununca da açılır
													<Tooltip
														title={intl.formatMessage({ id: "no-stock" })}
														arrow
														placement="top"
														enterTouchDelay={0}
													>
														<span className="ticket-card__list-item-soldout">
															<IconButton
																disabled
																size="small"
																className="ticket-card__list-item-icon"
																aria-label={intl.formatMessage({ id: "no-stock" })}
															>
																<DoNotDisturbIcon fontSize="small" />
															</IconButton>
														</span>
													</Tooltip>
												) : (
													<Tooltip
														title={intl.formatMessage({ id: "buy" })}
														arrow
														placement="top"
													>
														<IconButton
															size="small"
															onClick={() => {
																addToCart(childActivePeriod, p);
																setCartOpen(true);
															}}
															className="ticket-card__list-item-icon ticket-card__list-item-icon--cta no-toggle"
															aria-label={`${intl.formatMessage({
																id: "buy",
															})}: ${formattedTranslationValue(
																p.translations,
																"name",
																language
															)}`}
															sx={{
														color: eventButtonColor,
																backgroundColor: color,
																"&:hover": { opacity: 0.85, backgroundColor: color },
															}}
														>
															<AddRoundedIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											</Box>
										</Box>
									);
								})}
							</Box>
						</Box>
					</Box>
				</Box>
			)}

			{open && selectedPkg && (
				<Modal open={open} aria-labelledby="modal-title" onClose={handleClose}>
					<Box className="widget-block--form--modal">
						<Box sx={{ textAlign: "center", minHeight: "40px" }}>
							<IconButton
								onClick={handleClose}
								aria-label={intl.formatMessage({ id: "close" })}
								sx={{ position: "absolute", top: 8, right: 8, cursor: "pointer" }}
							>
								<CloseIcon />
							</IconButton>
							<Typography
								id="modal-title"
								variant="span"
								className="widget-block--form--modal--title"
							>
								{formattedTranslationValue(selectedPkg.translations, "name", language)}
							</Typography>
						</Box>
						<Divider />
						<Box className="widget-block--form--modal--details--container">
							<Box
								className="widget-block--form--modal--description"
								dangerouslySetInnerHTML={{
									__html: formattedTranslationValue(
										selectedPkg.translations,
										"description",
										language
									),
								}}
							/>
						</Box>
						<Box className="widget-block--form--modal--details--close">
							<Button
								startIcon={<CloseIcon sx={{ color }} />}
								onClick={handleClose}
								variant="outlined"
								sx={{
									color: "#000",
									borderColor: color,
									fontSize: "0.75em",
									"&:hover": { opacity: 0.8 },
								}}
							>
								<FormattedMessage id="close" />
							</Button>
							{/* Modal hangi bilet için açıldıysa (ana paket ya da alt bilet)
							    onun stok durumu kontrol edilir ve sepete o eklenir */}
							{isOutOfStock(selectedPkg.activePrice, selectedPkg, cart) ? (
								<Button
									variant="contained"
									disabled
									startIcon={<DoNotDisturbIcon />}
									sx={{ fontSize: "0.75em" }}
								>
									<FormattedMessage id="no-stock" />
								</Button>
							) : (
								<Button
									variant="contained"
									startIcon={<ShoppingCartIcon />}
									sx={{
										backgroundColor: color,
										color: eventButtonColor,
										"&:hover": { opacity: 0.85, backgroundColor: color },
										fontSize: "0.75em",
									}}
									onClick={() => {
										addToCart(selectedPkg.activePrice, selectedPkg);
										setCartOpen(true);
										handleClose();
									}}
									className="no-toggle"
								>
									<FormattedMessage id="buy" />
								</Button>
							)}
						</Box>
					</Box>
				</Modal>
			)}
		</>
	);
};

export default Package;
